if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        /* GYEB 9-26: apps can inject extra <form>s and submit buttons inside
           <product-form>. Bind to the /cart/add form that owns the visible
           Add to cart button, and use THAT button, not the first match. */
        this.form = Array.from(this.querySelectorAll('form')).find((form) =>
          /\/cart\/add/.test(form.getAttribute('action') || '') && form.querySelector('[type="submit"][name="add"]')
        ) || this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton =
          this.form.querySelector('[type="submit"][name="add"]') || this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      /* GYEB 2026-08-15 -- duplicate add-to-cart hardening.
         Every control that can submit THIS form, including buttons that sit
         OUTSIDE <product-form> and target it via the HTML form="" attribute
         (the mobile sticky bar). Stock Dawn only ever disabled the in-form
         button, so an out-of-form button showed no feedback, the shopper
         assumed the tap missed, and tapped again. */
      get allSubmitButtons() {
        var list = [];
        var inForm = this.querySelectorAll('[type="submit"]');
        for (var i = 0; i < inForm.length; i++) list.push(inForm[i]);
        // Lock every Add to Cart control on the page. Duplicate PDP forms or
        // custom sticky CTAs must show the same pending state as the form that
        // owns the request.
        var pageButtons = document.querySelectorAll(
          'form[action*="/cart/add"] button[type="submit"][name="add"], ' +
          'form[action*="/cart/add"] input[type="submit"][name="add"], ' +
          'button[form][type="submit"][name="add"], input[form][type="submit"][name="add"]'
        );
        for (var j = 0; j < pageButtons.length; j++) {
          if (list.indexOf(pageButtons[j]) === -1) list.push(pageButtons[j]);
        }
        return list;
      }

      setBusy(isBusy) {
        window.GYEB_CART_BUSY = !!isBusy;
        this.allSubmitButtons.forEach(function (btn) {
          if (isBusy) {
            if (btn.disabled) return; // sold out / already off -- leave it alone
            btn.setAttribute('data-gyeb-busy', 'true');
            btn.disabled = true;
            btn.setAttribute('aria-disabled', 'true');
            btn.classList.add('loading');
          } else if (btn.getAttribute('data-gyeb-busy') === 'true') {
            btn.removeAttribute('data-gyeb-busy');
            btn.disabled = false;
            btn.removeAttribute('aria-disabled');
            btn.classList.remove('loading');
          }
        });
        var spinner = this.querySelector('.loading__spinner');
        if (spinner) spinner.classList.toggle('hidden', !isBusy);
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        // Per-form guard (stock Dawn) ...
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;
        // ... plus a document-wide guard so a quick-add card, the sticky bar
        // and the main buy box can never post to /cart/add.js concurrently.
        if (window.GYEB_CART_BUSY) return;

        this.handleErrorMessage();

        this.setBusy(true);

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        /* D-008 (2026-09-10, test theme only): one-tap Dynamic Duo upgrade.
           If the upgrade box is ticked AND Original is still the posted
           variant, post ONE Dynamic Duo instead of the Edge Control. Never
           both. Selling plan is dropped because it belongs to Edge Control.
           Sticky buttons that submit this form via form="" pass through here
           too. ROLLBACK: delete this block. */
        const d008Box = this.form.querySelector('[data-d008-upgrade]');
        if (d008Box && d008Box.checked && !d008Box.disabled &&
            String(formData.get('id')) === d008Box.dataset.sourceVariant) {
          formData.set('id', d008Box.dataset.duoVariant);
          formData.set('quantity', '1');
          formData.delete('selling_plan');
        }
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;
        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            }

            // Shopify returned a successful add response. If later cart rendering
            // fails, this is still an added item and must not be presented as a
            // failed click that the shopper should retry.
            if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
            /* GYEB: a dropped request is the dangerous case. The add may have
               landed server-side even though we never saw the response, so we
               must NOT re-arm the button instantly -- that is how a shopper on
               a flaky in-app browser ends up with 7 units. */
            this.networkError = true;
            this.handleErrorMessage(
              'Connection dropped. Open your cart to check before adding again.'
            );
          })
          .finally(() => {
            const cooldown = this.networkError ? 2500 : 0;
            const self = this;
            setTimeout(function () {
              if (self.cart && self.cart.classList.contains('is-empty')) self.cart.classList.remove('is-empty');
              if (self.error) {
                // Sold out: keep the button off, but stop the spinner.
                window.GYEB_CART_BUSY = false;
                self.allSubmitButtons.forEach(function (btn) {
                  btn.removeAttribute('data-gyeb-busy');
                  btn.classList.remove('loading');
                });
                var spinner = self.querySelector('.loading__spinner');
                if (spinner) spinner.classList.add('hidden');
              } else {
                self.setBusy(false);
              }
              self.networkError = false;
            }, cooldown);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
